const MS_PER_DAY = 1000 * 60 * 60 * 24;

const dateKeyToUtcTime = (dateKey: string): number | null => {
    const [year, month, day] = dateKey.split('-').map(Number);
    if (!year || !month || !day) return null;
    return Date.UTC(year, month - 1, day);
};

export function getDaysOld(dateString?: string | null, timeZone?: string | null): number | null {
    if (!dateString) return null;

    const givenDateKey = formatDateInTimezone(dateString, timeZone);
    const todayDateKey = formatDateInTimezone(new Date().toISOString(), timeZone);
    if (!givenDateKey || !todayDateKey) return null;

    const givenDateUtc = dateKeyToUtcTime(givenDateKey);
    const todayUtc = dateKeyToUtcTime(todayDateKey);
    if (givenDateUtc === null || todayUtc === null) return null;

    const diffInDays = Math.floor((todayUtc - givenDateUtc) / MS_PER_DAY);

    return diffInDays >= 0 ? diffInDays : 0;
}

const DATE_TIME_FORMAT_OPTIONS: Intl.DateTimeFormatOptions = {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
};

export function formatDateTime(dateString?: string | null, timeZone?: string | null): string {
    if (!dateString) return '-';

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return '-';

    try {
        return new Intl.DateTimeFormat('en-US', {
            ...DATE_TIME_FORMAT_OPTIONS,
            ...(timeZone ? { timeZone } : {}),
        }).format(date);
    } catch (_error) {
        return date.toLocaleString('en-US', DATE_TIME_FORMAT_OPTIONS);
    }
}

export function formatDateInTimezone(dateString?: string | null, timeZone?: string | null): string | null {
    if (!dateString || !timeZone) return null;

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return null;

    try {
        const parts = new Intl.DateTimeFormat('en-CA', {
            timeZone,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        }).formatToParts(date);
        const year = parts.find((part) => part.type === 'year')?.value;
        const month = parts.find((part) => part.type === 'month')?.value;
        const day = parts.find((part) => part.type === 'day')?.value;
        return year && month && day ? `${year}-${month}-${day}` : null;
    } catch (_error) {
        return null;
    }
}
export function formatTicketNumber(
    ticket?: string | number | null
): string {
    if (ticket === null || ticket === undefined || ticket === '') return '-';

    const value = String(ticket);

    if (value.length <= 4) return value;

    const part1 = value.slice(0, 4);
    const part2 = value.slice(4, 10);
    const part3 = value.slice(10);

    return [part1, part2, part3].filter(Boolean).join('-');
}

export function formatTicketNumberForCsv(
    ticket?: string | number | null
): string {
    if (ticket === null || ticket === undefined || ticket === '') return '';

    return `'${String(ticket)}`;
}

export function downloadCsvFile(
    rows: Array<Record<string, unknown>>,
    filename: string
): void {
    if (!rows.length) {
        return;
    }

    const headers = Object.keys(rows[0]);
    const pad2 = (value: number): string => String(value).padStart(2, '0');
    const isDateLikeHeader = (header: string): boolean =>
        header.endsWith('_date') || header.endsWith('_at') || header.includes('date_time');
    const formatUsDate = (date: Date): string =>
        `${pad2(date.getMonth() + 1)}/${pad2(date.getDate())}/${date.getFullYear()}`;
    const formatUsDateTime = (date: Date): string => {
        const hours24 = date.getHours();
        const hours12 = hours24 % 12 || 12;
        const meridiem = hours24 >= 12 ? 'PM' : 'AM';

        return `${formatUsDate(date)} ${hours12}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())} ${meridiem}`;
    };
    const normalizeCsvValue = (header: string, value: unknown): string | number | boolean => {
        if (value === null || value === undefined) {
            return '';
        }

        if (typeof value === 'number' || typeof value === 'boolean') {
            return value;
        }

        if (typeof value === 'string' && isDateLikeHeader(header)) {
            const parsed = new Date(value);
            if (!Number.isNaN(parsed.getTime())) {
                const hasTimeComponent =
                    header.endsWith('_at') ||
                    header.includes('date_time') ||
                    value.includes('T') ||
                    /\d{1,2}:\d{2}/.test(value);

                return hasTimeComponent ? formatUsDateTime(parsed) : formatUsDate(parsed);
            }
        }

        return typeof value === 'string' ? value : String(value);
    };
    const escapeCsvValue = (value: unknown): string => {
        if (value === null || value === undefined) {
            return '';
        }

        const normalized = typeof value === 'string' ? value : String(value);
        if (/[",\n]/.test(normalized)) {
            return `"${normalized.replace(/"/g, '""')}"`;
        }
        return normalized;
    };

    const csvLines = [
        headers.join(','),
        ...rows.map((row) =>
            headers
                .map((header) => escapeCsvValue(normalizeCsvValue(header, row[header])))
                .join(',')
        ),
    ];

    const blob = new Blob([csvLines.join('\n')], {
        type: 'text/csv;charset=utf-8;',
    });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
}
export * from './currentInventoryPackScopeQuery';
export * from './currentInventoryViewUtils';
export * from './currentInventoryCsvUtils';
export * from './currentInventoryStatusPageUtils';
